import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFactory } from '../../../../context/FactoryContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useCurrentEmployee } from '../../../../hooks/useCurrentEmployee';
import {
  canManageFactoryTeams,
  isProdSupervisorTeamScoped,
} from '../../../../utils/roleChecks';
import { filterTeamsByRole } from '../../../../utils/teamScope';
import { teamService } from '../services/teamService';
import { employeeService } from '../services/employeeService';
import { matchesEmployeeSearch } from '../utils/employeeDepartment';

function resolveFactoryId(selectedFactoryCode, factoriesList) {
  if (!selectedFactoryCode || !Array.isArray(factoriesList) || factoriesList.length === 0) {
    return null;
  }
  const code = String(selectedFactoryCode).toUpperCase();
  const found = factoriesList.find((f) => {
    const c = (f.asset_code || f.assetCode || f.Code || f.code || f.name || '').toUpperCase();
    return c === code;
  });
  if (!found) return null;
  return found.id ?? found.ID ?? null;
}

/**
 * @param {Array|null} employeesFromList — lista ya cargada (p. ej. desde useEmployees);
 *   si se pasa, los selectores usan los mismos empleados que la tabla.
 */
export function useTeams(employeesFromList = null) {
  const queryClient = useQueryClient();
  const { selectedFactory, factories } = useFactory();
  const { roleKeys, isLoading: permissionsLoading } = usePermissions();
  const { employeeId: currentEmployeeId, isReady: currentEmployeeReady } = useCurrentEmployee();
  const factoryBase = selectedFactory || '';
  const factoryId = useMemo(
    () => resolveFactoryId(selectedFactory, factories),
    [selectedFactory, factories]
  );

  const isSupervisorTeamScoped = useMemo(
    () => isProdSupervisorTeamScoped(roleKeys),
    [roleKeys]
  );
  const canManageTeams = useMemo(() => canManageFactoryTeams(roleKeys), [roleKeys]);
  const teamsQueryReady = !isSupervisorTeamScoped || currentEmployeeReady;

  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [actionError, setActionError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const {
    data: allTeams = [],
    isLoading: teamsLoading,
    error: teamsError,
    refetch: refetchTeams,
  } = useQuery({
    queryKey: ['labor-teams'],
    queryFn: () => teamService.getTeams(),
    staleTime: 60_000,
    enabled: teamsQueryReady,
  });

  const teams = useMemo(
    () =>
      filterTeamsByRole(allTeams, {
        factoryId,
        roleKeys,
        leaderEmployeeId: currentEmployeeId,
      }),
    [allTeams, factoryId, roleKeys, currentEmployeeId]
  );

  const selectedTeam = useMemo(
    () => teams.find((t) => String(t.id) === String(selectedTeamId)) ?? null,
    [teams, selectedTeamId]
  );

  const { data: factoryStructure = [] } = useQuery({
    queryKey: ['factory-depart-roles'],
    queryFn: () => employeeService.getFactoryDepartRoles(),
    staleTime: 300_000,
  });

  const factoryDepartments = useMemo(() => {
    if (!factoryId) return [];
    const factory = factoryStructure.find((f) => Number(f.id) === Number(factoryId));
    return factory?.departments ?? [];
  }, [factoryStructure, factoryId]);

  const { data: fetchedEmployees = [] } = useQuery({
    queryKey: ['factory-employees-for-teams', factoryBase],
    queryFn: () => employeeService.getEmployees(factoryBase, true),
    enabled: employeesFromList == null && Boolean(factoryBase),
    staleTime: 120_000,
  });

  const factoryEmployees = employeesFromList ?? fetchedEmployees;

  const {
    data: members = [],
    isLoading: membersLoading,
    error: membersError,
    refetch: refetchMembers,
  } = useQuery({
    queryKey: ['team-members', selectedTeamId],
    queryFn: () => teamService.getTeamMembers(selectedTeamId),
    enabled: Boolean(selectedTeamId),
  });

  const memberEmployeeIds = useMemo(
    () =>
      new Set(
        members.map((m) => String(m.employee_id || m.employee?.id || m.Employee?.id)).filter(Boolean)
      ),
    [members]
  );

  const eligibleMembers = useMemo(() => {
    let filtered = factoryEmployees.filter((emp) => !memberEmployeeIds.has(String(emp.id)));
    if (memberSearchQuery.trim()) {
      filtered = filtered.filter((emp) => matchesEmployeeSearch(emp, memberSearchQuery));
    }
    return filtered;
  }, [factoryEmployees, memberEmployeeIds, memberSearchQuery]);

  useEffect(() => {
    setSelectedTeamId('');
    setMemberSearchQuery('');
    setActionError(null);
    setActionMessage(null);
  }, [factoryId]);

  useEffect(() => {
    setMemberSearchQuery('');
  }, [selectedTeamId]);

  useEffect(() => {
    if (isSupervisorTeamScoped && teams.length === 1) {
      setSelectedTeamId(String(teams[0].id));
    }
  }, [isSupervisorTeamScoped, teams]);

  const invalidateTeams = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['labor-teams'] });
  }, [queryClient]);

  const invalidateTeamMembers = useCallback(
    (teamId) => {
      if (teamId) {
        queryClient.invalidateQueries({ queryKey: ['team-members', String(teamId)] });
      }
    },
    [queryClient]
  );

  const createTeam = useCallback(
    async ({ name, description, departmentId, leaderId }) => {
      setActionError(null);
      setActionMessage(null);
      if (!factoryId) throw new Error('Selecciona una fábrica en el header.');
      const team = await teamService.createTeam({
        factory_id: Number(factoryId),
        department_id: Number(departmentId),
        leader_id: Number(leaderId),
        name: String(name).trim(),
        description: description?.trim() || '',
      });
      invalidateTeams();
      if (team?.id) invalidateTeamMembers(team.id);
      setActionMessage('Equipo creado correctamente.');
      return team;
    },
    [factoryId, invalidateTeams, invalidateTeamMembers]
  );

  const updateTeam = useCallback(
    async (teamId, { name, description, leaderId }) => {
      setActionError(null);
      setActionMessage(null);
      const payload = {};
      if (name?.trim()) payload.name = name.trim();
      if (description !== undefined) payload.description = description.trim();
      if (leaderId) payload.leader_id = Number(leaderId);
      const team = await teamService.updateTeam(teamId, payload);
      invalidateTeams();
      invalidateTeamMembers(teamId);
      setActionMessage('Equipo actualizado.');
      return team;
    },
    [invalidateTeams, invalidateTeamMembers]
  );

  const deleteTeam = useCallback(
    async (teamId) => {
      setActionError(null);
      setActionMessage(null);
      await teamService.deleteTeam(teamId);
      if (String(selectedTeamId) === String(teamId)) {
        setSelectedTeamId('');
      }
      invalidateTeams();
      setActionMessage('Equipo eliminado.');
    },
    [invalidateTeams, selectedTeamId]
  );

  const addMember = useCallback(
    async ({ employeeId, startDate }) => {
      setActionError(null);
      setActionMessage(null);
      if (!selectedTeamId) throw new Error('Selecciona un equipo.');
      await teamService.addTeamMember(selectedTeamId, {
        employeeId,
        startDate: new Date(startDate).toISOString(),
      });
      refetchMembers();
      setActionMessage('Miembro añadido al equipo.');
    },
    [selectedTeamId, refetchMembers]
  );

  const removeMember = useCallback(
    async (employeeId) => {
      setActionError(null);
      setActionMessage(null);
      if (!selectedTeamId) return;
      await teamService.removeTeamMember(selectedTeamId, employeeId);
      refetchMembers();
      setActionMessage('Miembro eliminado del equipo.');
    },
    [selectedTeamId, refetchMembers]
  );

  const runAction = useCallback(async (fn) => {
    try {
      setActionError(null);
      return await fn();
    } catch (err) {
      setActionError(err?.message || 'Error en la operación.');
      throw err;
    }
  }, []);

  return {
    factoryId,
    selectedFactory,
    teams,
    teamsLoading: teamsLoading || permissionsLoading,
    teamsError,
    refetchTeams,
    factoryDepartments,
    factoryEmployees,
    eligibleMembers,
    selectedTeam,
    selectedTeamId,
    setSelectedTeamId,
    memberSearchQuery,
    setMemberSearchQuery,
    members,
    membersLoading,
    membersError,
    createTeam,
    updateTeam,
    deleteTeam,
    addMember,
    removeMember,
    actionError,
    actionMessage,
    setActionMessage,
    setActionError,
    runAction,
    canManageTeams,
    isSupervisorTeamScoped,
  };
}
